import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Card from '../../../components/base/Card';
import Button from '../../../components/base/Button';
import Input from '../../../components/base/Input';
import Select from '../../../components/base/Select';
import { supabase, generateTrackingId } from '../../../utils/supabase';
import { useAuth } from '../../../hooks/useAuth';
import LoginModal from '../../../components/feature/LoginModal';

interface FormData {
  sender_name: string;
  receiver_name: string;
  origin: string;
  destination: string;
  weight: string;
  mode_of_transportation: string;
  remarks: string;
}

export default function CreateShipment() {
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState<FormData>({
    sender_name: '',
    receiver_name: '',
    origin: '',
    destination: '',
    weight: '',
    mode_of_transportation: 'Air',
    remarks: ''
  });
  const [errors, setErrors] = useState<Partial<FormData>>({});

  // Check authentication
  useEffect(() => {
    if (!user) {
      setShowLoginModal(true);
    }
  }, [user]);

  const handleLogin = (userData: any) => {
    login(userData);
    setShowLoginModal(false);
  };

  // Show login modal if not authenticated
  if (!user) {
    return (
      <LoginModal 
        isOpen={showLoginModal}
        onClose={() => navigate('/admin')}
        onLogin={handleLogin}
      />
    );
  }

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Get weight unit based on mode of transportation
  const getWeightUnit = () => {
    return formData.mode_of_transportation === 'Air' ? 'kg' : 'cbm';
  };

  // Get weight label based on mode of transportation
  const getWeightLabel = () => {
    return formData.mode_of_transportation === 'Air' ? 'Weight (kg)' : 'Volume (cbm)';
  };

  // Get weight placeholder based on mode of transportation
  const getWeightPlaceholder = () => {
    return formData.mode_of_transportation === 'Air' 
      ? 'Enter weight in kilograms' 
      : 'Enter volume in cubic meters';
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<FormData> = {};

    if (!formData.sender_name.trim()) newErrors.sender_name = 'Sender name is required';
    if (!formData.receiver_name.trim()) newErrors.receiver_name = 'Receiver name is required';
    if (!formData.origin.trim()) newErrors.origin = 'Origin is required';
    if (!formData.destination.trim()) newErrors.destination = 'Destination is required';
    if (!formData.weight.trim()) {
      newErrors.weight = formData.mode_of_transportation === 'Air' 
        ? 'Weight is required' 
        : 'Volume is required';
    } else if (isNaN(Number(formData.weight)) || Number(formData.weight) <= 0) {
      newErrors.weight = formData.mode_of_transportation === 'Air'
        ? 'Weight must be a positive number'
        : 'Volume must be a positive number';
    }
    if (!formData.mode_of_transportation) newErrors.mode_of_transportation = 'Mode of transportation is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    setError('');

    try {
      // Generate unique tracking ID with new format
      let trackingId = generateTrackingId();
      let isUnique = false;
      let attempts = 0;
      const maxAttempts = 10;

      // Ensure tracking ID is unique
      while (!isUnique && attempts < maxAttempts) {
        const { data: existingShipment } = await supabase
          .from('shipments')
          .select('tracking_id')
          .eq('tracking_id', trackingId)
          .single();

        if (!existingShipment) {
          isUnique = true;
        } else {
          trackingId = generateTrackingId();
          attempts++;
        }
      }

      if (!isUnique) {
        throw new Error('Unable to generate unique tracking ID. Please try again.');
      }

      const { data, error } = await supabase
        .from('shipments')
        .insert([{
          tracking_id: trackingId,
          sender_name: formData.sender_name.trim(),
          receiver_name: formData.receiver_name.trim(),
          origin: formData.origin.trim(),
          destination: formData.destination.trim(),
          weight: Number(formData.weight),
          mode_of_transportation: formData.mode_of_transportation,
          status: 'Created',
          remarks: formData.remarks.trim() || null,
          created_by: user.full_name,
          created_by_email: user.email,
          created_at_timestamp: new Date().toISOString()
        }])
        .select()
        .single();

      if (error) throw error;

      // Create initial update
      await supabase
        .from('shipment_updates')
        .insert([{
          tracking_id: trackingId,
          location: formData.origin.trim(),
          status: 'Created',
          remarks: 'Shipment created and ready for processing'
        }]);

      navigate(`/admin/shipment/${trackingId}`);
    } catch (error) {
      console.error('Error creating shipment:', error);
      setError('Failed to create shipment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center space-x-4 mb-4">
                <Button
                  onClick={() => navigate('/')}
                  variant="outline"
                  className="whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line mr-2"></i>
                  Back to Homepage
                </Button>
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Create New Shipment
              </h1>
              <p className="text-gray-600">Add a new shipment to the tracking system</p>
            </div>
          </div>
        </div>

        <Card>
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
                {error}
              </div>
            )}
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                label="Sender Name"
                value={formData.sender_name}
                onChange={(e) => handleInputChange('sender_name', e.target.value)}
                error={errors.sender_name}
                placeholder="Enter sender's full name"
                required
              />
              
              <Input
                label="Receiver Name"
                value={formData.receiver_name}
                onChange={(e) => handleInputChange('receiver_name', e.target.value)}
                error={errors.receiver_name}
                placeholder="Enter receiver's full name"
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                label="Origin"
                value={formData.origin}
                onChange={(e) => handleInputChange('origin', e.target.value)}
                error={errors.origin}
                placeholder="e.g., Lagos"
                required
              />
              
              <Input
                label="Destination"
                value={formData.destination}
                onChange={(e) => handleInputChange('destination', e.target.value)}
                error={errors.destination}
                placeholder="e.g., Valletta"
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Select
                label="Mode of Transportation"
                value={formData.mode_of_transportation}
                onChange={(e) => handleInputChange('mode_of_transportation', e.target.value)}
                error={errors.mode_of_transportation}
                options={[
                  { value: 'Air', label: 'Air' },
                  { value: 'Sea', label: 'Sea' }
                ]}
                required
              />

              <Input
                label={getWeightLabel()}
                type="number"
                step="0.01"
                min="0"
                value={formData.weight}
                onChange={(e) => handleInputChange('weight', e.target.value)}
                error={errors.weight}
                placeholder={getWeightPlaceholder()}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Remarks (Optional)
              </label>
              <textarea
                value={formData.remarks}
                onChange={(e) => handleInputChange('remarks', e.target.value)}
                placeholder="Additional notes about the shipment..."
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors text-sm"
              />
            </div>

            <div className="flex justify-end space-x-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/admin')}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                loading={loading}
              >
                Create Shipment
              </Button>
            </div>
          </form>
        </Card>
      </main>
    </div>
  );
}
